import React, { useCallback, useEffect, useMemo, useState } from "react";
import EpsfPageBreadcrumb from "./EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import Dropdown from "../../common/dropDown";
import { EpsfAccessGate, useEpsfAccess } from "./epsfAccess";
import {
  batchOptionLabel,
  findOption,
  renderImpProductCell,
  useEpsfStudySite,
} from "./epsfLedgerUi";
import {
  fetchImpBalances,
  fetchInspectionPack,
  postImpRecall,
} from "./epsfService";
import "./EpsfPages.scss";

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const RECALL_FIELD_OPTIONS = [
  { key: "Product", label: "IMP" },
  { key: "BatchNumber", label: "Batch" },
  { key: "OnHand", label: "On hand" },
  { key: "RecallReason", label: "Reason" },
];

const RECALL_DEFAULT_COLUMNS = [
  "Product",
  "BatchNumber",
  "OnHand",
  "RecallReason",
];

export default function EpsfInspection() {
  const { loading, canView, moduleAccess, canUnblinded, canDestroy } = useEpsfAccess();
  const allow = canView && moduleAccess;
  const ctx = useEpsfStudySite();

  const [pack, setPack] = useState(null);
  const [balances, setBalances] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [recallBatchId, setRecallBatchId] = useState("");
  const [recallNotes, setRecallNotes] = useState("");

  const batchOptions = useMemo(
    () =>
      balances
        .filter((b) => !(b.IsRecalled || b.isRecalled))
        .map((b) => ({
          value: String(b.ImpBatchId || b.impBatchId),
          label: batchOptionLabel(b),
        })),
    [balances],
  );

  const batchOptionsWithPlaceholder = useMemo(
    () => [{ value: "", label: "Select batch" }, ...batchOptions],
    [batchOptions],
  );

  const recalledRows = useMemo(
    () => balances.filter((b) => b.IsRecalled || b.isRecalled),
    [balances],
  );

  const refresh = useCallback(async () => {
    setError("");
    if (!ctx.studyId || !ctx.siteId) {
      setPack(null);
      setBalances([]);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const [insp, bal] = await Promise.all([
        fetchInspectionPack(ctx.studyId, ctx.siteId),
        fetchImpBalances(ctx.studyId, ctx.siteId),
      ]);
      if (insp.success) setPack(insp);
      else if (insp.message) setError(insp.message);
      if (bal.success) setBalances(bal.rows || []);
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onRecall = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    if (!recallBatchId) {
      setError("Select a batch to recall.");
      return;
    }
    setBusy(true);
    try {
      const result = await postImpRecall({
        StudyId: ctx.studyId,
        SiteId: ctx.siteId,
        ImpBatchId: recallBatchId,
        Notes: recallNotes.trim(),
      });
      if (!result.success) {
        setError(result.message || "Recall failed.");
        return;
      }
      setMessage(result.message || "Batch recalled.");
      setRecallNotes("");
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const onExport = () => {
    if (!pack) return;
    downloadJson(
      `epsf-inspection-${ctx.studyId}-${ctx.siteId}.json`,
      {
        exportedAt: new Date().toISOString(),
        studyId: ctx.studyId,
        siteId: ctx.siteId,
        custody: pack.custody,
        txns: canUnblinded
          ? pack.txns
          : (pack.txns || []).map((t) => ({
              ...t,
              ReferenceNo: t.ReferenceNo ? "[redacted]" : null,
              Notes: t.Notes ? "[redacted]" : null,
              ParticipantId: null,
            })),
        tempReadings: pack.tempReadings,
        formOccurrences: pack.formOccurrences,
        unblindedIncluded: !!canUnblinded,
      },
    );
  };

  const renderRecallCell = useCallback((row, field) => {
    if (field === "Product") {
      return renderImpProductCell(row);
    }
    if (field === "BatchNumber") {
      return row.BatchNumber || row.batchNumber || "—";
    }
    if (field === "OnHand") {
      const v = row.OnHand ?? row.onHand;
      return v == null || v === "" ? "—" : String(v);
    }
    if (field === "RecallReason") {
      return row.RecallReason || row.recallReason || "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "Inspection & recall" },
          ]}
        />
        <div className="epsf-receive">
          <p className="epsf-hub__hint" style={{ marginTop: 0 }}>
            Inspection pack exports custody, ledger, temperature, and form dues for this study-site.
            Unblinded fields are redacted unless you have <code>EPSF_UNBLINDED</code>.
            IRT reconcile remains a later integration stub.
          </p>
          {error ? <p className="epsf-receive__error">{error}</p> : null}
          {message ? <p className="epsf-receive__ok">{message}</p> : null}

          <div className="epsf-custody" aria-label="Inspection custody">
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">
                {pack?.custody?.Received ?? pack?.custody?.received ?? "—"}
              </span>
              <span className="epsf-custody__label">Received</span>
            </div>
            <div className="epsf-custody__stat is-emphasis">
              <span className="epsf-custody__value">
                {pack?.custody?.OnHand ?? pack?.custody?.onHand ?? "—"}
              </span>
              <span className="epsf-custody__label">On hand</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">
                {pack?.custody?.Dispensed ?? pack?.custody?.dispensed ?? "—"}
              </span>
              <span className="epsf-custody__label">Dispensed</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">
                {pack?.custody?.RecallEvents ?? pack?.custody?.recallEvents ?? "—"}
              </span>
              <span className="epsf-custody__label">Recall events</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">{pack?.txns?.length ?? 0}</span>
              <span className="epsf-custody__label">Txn rows</span>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onExport}
            disabled={!pack}
            style={{ marginTop: "0.9rem" }}
          >
            Download inspection pack (JSON)
          </button>

          {(canDestroy || canView) && (
            <form className="epsf-receive__form" onSubmit={onRecall} style={{ marginTop: "1.25rem" }}>
              <h3>Recall batch</h3>
              <div className="row g-3">
                <div className="col-12">
                  <div className="epsf-form-field">
                    <label className="form-label">Batch on hand</label>
                    <Dropdown
                      options={batchOptionsWithPlaceholder}
                      selected={findOption(
                        batchOptionsWithPlaceholder,
                        recallBatchId,
                      )}
                      onSelect={(opt) => setRecallBatchId(opt?.value || "")}
                      showSearch
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="epsf-form-field">
                    <label className="form-label">Recall reason</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={recallNotes}
                      onChange={(ev) => setRecallNotes(ev.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving..." : "Post recall"}
              </button>
            </form>
          )}

          <div className="epsf-report-wrap">
            <h3>Recalled / quarantined batches</h3>
            <ReportTable
              rows={recalledRows}
              loading={listLoading}
              fieldOptions={RECALL_FIELD_OPTIONS}
              defaultColumns={RECALL_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                RECALL_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
              }
              renderCell={renderRecallCell}
              getRowKey={(row, index) =>
                String(row.ImpBatchId || row.impBatchId || index)
              }
              persistLayoutKey="epsf-inspection-recalled-v2"
              emptyMessage="No recalled batches."
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>
    </EpsfAccessGate>
  );
}
